import { Component, OnInit, TemplateRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  LogicielCaisseTicketTemplateService,
  TicketTemplateBlock,
  TicketTemplateLayout,
  TicketTemplateRow,
} from 'app/services/logiciel-caisse/logiciel-caisse-ticket-template.service';
import {
  TICKET_FIELD_PALETTE,
  TicketFieldPaletteItem,
  newBlockId,
} from 'app/services/logiciel-caisse/ticket-template-field-catalog';
import {
  buildTicketTestPreviewHtml,
  printTicketTest,
  TicketTestPrintContext,
} from 'app/services/logiciel-caisse/ticket-template-test-print';

@Component({
  selector: 'app-logiciel-caisse-ticket-templates',
  templateUrl: './logiciel-caisse-ticket-templates.component.html',
  styleUrls: ['./logiciel-caisse-ticket-templates.component.scss'],
})
export class LogicielCaisseTicketTemplatesComponent implements OnInit {
  rows: TicketTemplateRow[] = [];
  loading = false;
  saving = false;
  selected: TicketTemplateRow | null = null;
  editLayout: TicketTemplateLayout | null = null;
  readonly palette = TICKET_FIELD_PALETTE;
  testPreviewSafe: SafeHtml | null = null;
  private testPrintContext: TicketTestPrintContext | null = null;
  private testModalRef: NgbModalRef | null = null;

  constructor(
    private readonly templateService: LogicielCaisseTicketTemplateService,
    private readonly toastr: ToastrService,
    private readonly modalService: NgbModal,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadRows();
  }

  loadRows(): void {
    this.loading = true;
    this.templateService.listForStation().subscribe({
      next: (items) => {
        this.rows = items;
        this.loading = false;
        if (this.selected) {
          const refreshed = items.find((r) => r.code === this.selected?.code);
          if (refreshed) {
            this.selectRow(refreshed);
          }
        } else if (items.length > 0) {
          this.selectRow(items[0]);
        }
      },
      error: (err: Error) => {
        this.loading = false;
        this.toastr.error(err.message);
      },
    });
  }

  selectRow(row: TicketTemplateRow): void {
    this.selected = row;
    this.editLayout = this.cloneLayout(row.layout_json);
  }

  get sortedBlocks(): TicketTemplateBlock[] {
    if (!this.editLayout?.blocks) {
      return [];
    }
    return [...this.editLayout.blocks].sort((a, b) => a.order - b.order);
  }

  onDrop(event: CdkDragDrop<TicketTemplateBlock[]>): void {
    if (!this.editLayout) {
      return;
    }
    const blocks = this.sortedBlocks;
    moveItemInArray(blocks, event.previousIndex, event.currentIndex);
    blocks.forEach((b, i) => (b.order = i));
    this.editLayout = { ...this.editLayout, blocks };
  }

  addFromPalette(item: TicketFieldPaletteItem): void {
    if (!this.editLayout) {
      return;
    }
    const exists = this.editLayout.blocks.some(
      (b) => b.kind !== 'custom' && b.fieldKey === item.fieldKey && item.kind !== 'separator'
    );
    if (exists) {
      this.toastr.info('Ce champ est déjà dans le modèle.');
      return;
    }
    const order = this.editLayout.blocks.length;
    const block: TicketTemplateBlock = {
      id: newBlockId(),
      kind: item.kind,
      fieldKey: item.fieldKey,
      label: item.label,
      order,
      visible: true,
      ...(item.kind === 'custom' ? { value: 'Texte libre' } : {}),
    };
    this.editLayout = {
      ...this.editLayout,
      blocks: [...this.editLayout.blocks, block],
    };
  }

  removeBlock(block: TicketTemplateBlock): void {
    if (!this.editLayout) {
      return;
    }
    const blocks = this.editLayout.blocks
      .filter((b) => b.id !== block.id)
      .map((b, i) => ({ ...b, order: i }));
    this.editLayout = { ...this.editLayout, blocks };
  }

  toggleVisible(block: TicketTemplateBlock): void {
    block.visible = !block.visible;
  }

  onLargeurChange(value: string): void {
    if (!this.editLayout) {
      return;
    }
    const n = Number(value);
    this.editLayout.largeur_mm = Number.isFinite(n) && n >= 58 && n <= 120 ? n : 80;
  }

  testTicket(modal: TemplateRef<unknown>): void {
    if (!this.selected || !this.editLayout) {
      return;
    }
    this.testPrintContext = {
      typeCode: this.selected.code,
      typeLabel: this.selected.libelle,
      layout: this.editLayout,
    };
    this.testPreviewSafe = this.sanitizer.bypassSecurityTrustHtml(
      buildTicketTestPreviewHtml(this.testPrintContext)
    );
    this.testModalRef = this.modalService.open(modal, {
      size: 'md',
      centered: true,
      scrollable: true,
    });
  }

  printTestTicket(): void {
    if (!this.testPrintContext) {
      return;
    }
    printTicketTest(this.testPrintContext);
  }

  closeTestModal(): void {
    this.testModalRef?.close();
    this.testModalRef = null;
  }

  save(): void {
    if (!this.selected || !this.editLayout) {
      return;
    }
    this.saving = true;
    this.templateService.save(this.selected.code, this.editLayout).subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success('Modèle enregistré.');
        this.loadRows();
      },
      error: (err: Error) => {
        this.saving = false;
        this.toastr.error(err.message);
      },
    });
  }

  previewLine(block: TicketTemplateBlock): string {
    if (block.kind === 'separator') {
      return '────────────────────────';
    }
    if (block.kind === 'custom') {
      return block.value?.trim() || block.label;
    }
    return `{{ ${block.label} }}`;
  }

  private cloneLayout(layout: TicketTemplateLayout): TicketTemplateLayout {
    return {
      version: layout?.version ?? 1,
      largeur_mm: layout?.largeur_mm ?? 80,
      blocks: (layout?.blocks ?? []).map((b) => ({ ...b })),
    };
  }
}
